import { memo, useCallback, useState } from 'react';

import {
  ColorDropdownPicker,
  type ColorDropdownPickerCopy,
  type ColorDropdownPickerValue,
} from '@air/component-color-dropdown-picker';

import { WorkspaceAgentGuidedVariantColorOptionField } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantColorOptionField';
import { WorkspaceAgentGuidedVariantRoundedCornersPreview } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantRoundedCornersPreview';
import { WorkspaceAgentGuidedVariantRoundedCornersRadiusField } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantRoundedCornersRadiusField';
import { WorkspaceAgentGuidedVariantSettingsActions } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantSettingsActions';
import { type WorkspaceAgentGuidedVariantGenerateRequest } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/workspaceAgentGuidedVariantGenerateRequest';
import { type WorkspaceAgentGuidedVariantBrandKit } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantBrandKit';
import {
  getWorkspaceAgentGuidedVariantCanvasColor,
  getWorkspaceAgentGuidedVariantColorOptionLabel,
  WORKSPACE_AGENT_GUIDED_VARIANT_COLOR_OPTIONS,
  WORKSPACE_AGENT_GUIDED_VARIANT_DEFAULT_CUSTOM_COLOR,
  type WorkspaceAgentGuidedVariantColorOptionId,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantColor';
import { type WorkspaceAgentGuidedVariantImagePreviewSelection } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantImagePreviewSelection';
import {
  buildWorkspaceAgentRoundedCornersPrompt,
  getWorkspaceAgentRoundedCornersRadiusLabel,
  WORKSPACE_AGENT_ROUNDED_CORNERS_DEFAULT_RADIUS,
  type WorkspaceAgentRoundedCornersUnitId,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentRoundedCorners';

const ROUNDED_CORNERS_COLOR_PICKER_COPY: ColorDropdownPickerCopy = {
  customColor: 'Custom color',
  customColorInputLabel: 'Custom corner color',
  groupsTitle: 'Context colors',
};

export type WorkspaceAgentGuidedVariantRoundedCornersSettingsProps = {
  brandKits: readonly WorkspaceAgentGuidedVariantBrandKit[];
  isBrandKitLoading: boolean;
  onGenerate: (request: WorkspaceAgentGuidedVariantGenerateRequest) => void;
  selection: WorkspaceAgentGuidedVariantImagePreviewSelection;
};

export const WorkspaceAgentGuidedVariantRoundedCornersSettings = memo(
  ({ brandKits, isBrandKitLoading, onGenerate, selection }: WorkspaceAgentGuidedVariantRoundedCornersSettingsProps) => {
    const [cornerColorId, setCornerColorId] = useState<WorkspaceAgentGuidedVariantColorOptionId>('transparent');
    const [customColor, setCustomColor] = useState<ColorDropdownPickerValue | null>(null);
    const [radius, setRadius] = useState(WORKSPACE_AGENT_ROUNDED_CORNERS_DEFAULT_RADIUS);
    const [unit, setUnit] = useState<WorkspaceAgentRoundedCornersUnitId>('percent');
    const firstBrandColor = brandKits.flatMap((kit) => kit.colors)[0] ?? null;
    const effectiveCustomColor =
      customColor?.hex ?? firstBrandColor?.hex ?? WORKSPACE_AGENT_GUIDED_VARIANT_DEFAULT_CUSTOM_COLOR;
    const effectiveCustomLabel = customColor ? customColor.label : (firstBrandColor?.label ?? null);
    const isGenerateDisabled = selection.imageCount < 1;
    const generateLabel = `Generate ${selection.imageCount.toLocaleString()}`;
    const selectedColorLabel = getWorkspaceAgentGuidedVariantColorOptionLabel(cornerColorId);

    const handleCornerColorChange = useCallback((nextCornerColorId: WorkspaceAgentGuidedVariantColorOptionId) => {
      setCornerColorId(nextCornerColorId);
    }, []);

    const handleGenerate = useCallback(() => {
      if (selection.imageCount < 1) {
        return;
      }

      const color = getWorkspaceAgentGuidedVariantCanvasColor(cornerColorId, effectiveCustomColor);
      const radiusLabel = getWorkspaceAgentRoundedCornersRadiusLabel(radius, unit);
      let description = `${radiusLabel} · ${selectedColorLabel}`;

      if (cornerColorId === 'custom' && color) {
        description = effectiveCustomLabel
          ? `${radiusLabel} · ${effectiveCustomLabel} ${color}`
          : `${radiusLabel} · Custom ${color}`;
      }

      onGenerate({
        allowsJpeg: cornerColorId !== 'transparent',
        kind: 'content',
        build: (destination, output) =>
          buildWorkspaceAgentRoundedCornersPrompt({
            cornerColorId,
            customColor: effectiveCustomColor,
            destination,
            imageCount: selection.imageCount,
            output,
            radius,
            skippedFileCount: selection.skippedFileCount,
            skippedVideoCount: selection.skippedVideoCount,
            unit,
          }),
        structuredDisplay: {
          description,
          icon: 'square-round-corner',
          title: 'Rounded corners',
        },
      });
    }, [
      cornerColorId,
      effectiveCustomColor,
      effectiveCustomLabel,
      onGenerate,
      radius,
      selectedColorLabel,
      selection,
      unit,
    ]);

    return (
      <div className="flex flex-col gap-3" data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_ROUNDED_CORNERS_SETTINGS">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-3">
            <WorkspaceAgentGuidedVariantRoundedCornersRadiusField
              onRadiusChange={setRadius}
              onUnitChange={setUnit}
              radius={radius}
              unit={unit}
            />
            <WorkspaceAgentGuidedVariantColorOptionField
              legend="Corner color"
              onValueChange={handleCornerColorChange}
              options={WORKSPACE_AGENT_GUIDED_VARIANT_COLOR_OPTIONS}
              value={cornerColorId}
            />
            {cornerColorId === 'custom' ? (
              <ColorDropdownPicker
                colorGroups={brandKits}
                copy={ROUNDED_CORNERS_COLOR_PICKER_COPY}
                hex={effectiveCustomColor}
                isLoading={isBrandKitLoading}
                label={effectiveCustomLabel}
                onChange={setCustomColor}
              />
            ) : null}
          </div>
          <WorkspaceAgentGuidedVariantRoundedCornersPreview
            cornerColorId={cornerColorId}
            customColor={effectiveCustomColor}
            imageAlt={`${selectedColorLabel} corner preview`}
            previewHeight={selection.previewHeight}
            previewImageUrl={selection.previewImageUrl}
            previewWidth={selection.previewWidth}
            radius={radius}
            unit={unit}
          />
        </div>
        <WorkspaceAgentGuidedVariantSettingsActions
          generateLabel={generateLabel}
          isGenerateDisabled={isGenerateDisabled}
          onGenerate={handleGenerate}
        />
      </div>
    );
  },
);

WorkspaceAgentGuidedVariantRoundedCornersSettings.displayName = 'WorkspaceAgentGuidedVariantRoundedCornersSettings';
